import { z } from 'zod';
import {
  VendorExperienceLevel,
  VendorTeamSize,
  VendorVerificationStatus,
} from '../user/user.enums';
import { EquipmentCategory, MarketplaceCurrency } from './marketplace.enums';
import {
  PublicVendorListCategorySchema,
  PublicVendorListEquipmentItemSchema,
  PublicVendorListPortfolioItemSchema,
  PublicVendorListRateCardItemSchema,
} from './marketplace.schemas';

/** Workspace equipment row (full manifest; public API uses the same shape). */
export const WorkspaceEquipmentItemInputSchema = z.object({
  name: z.string().min(1).max(255),
  category: z.enum(EquipmentCategory),
  notes: z.string().max(2000).optional(),
});

export const WorkspacePortfolioItemInputSchema = z.object({
  title: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
  mediaUrl: z.url().optional(),
  externalUrl: z.url().optional(),
});

export const WorkspaceRateCardItemInputSchema = z.object({
  label: z.string().min(1).max(255),
  amount: z.number().min(0),
  currency: z.enum(MarketplaceCurrency).default(MarketplaceCurrency.NGN),
  notes: z.string().max(2000).optional(),
});

/**
 * Authenticated vendor workspace profile (owner view).
 * Includes verification workflow fields and raw availability; never returned on public routes.
 */
export const VendorProfileWorkspaceSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  companyName: z.string(),
  bio: z.string(),
  logoUrl: z.url().nullable(),
  experienceLevel: z.enum(VendorExperienceLevel),
  yearsExperience: z.number().int().nullable(),
  teamSize: z.enum(VendorTeamSize).nullable(),
  maxDistanceKm: z.number().int().nullable(),
  availabilityCalendar: z.record(z.string(), z.unknown()).nullable(),
  verificationStatus: z.enum(VendorVerificationStatus),
  verificationReason: z.string().nullable(),
  verifiedAt: z.iso.datetime().nullable(),
  visibleInMarketplace: z.boolean(),
  isAvailable: z.boolean(),
  defaultCurrency: z.enum(MarketplaceCurrency),
  averageRating: z.number(),
  totalReviews: z.number().int(),
  completionRate: z.number(),
  hasPin: z.boolean(),
  equipmentManifest: z.array(PublicVendorListEquipmentItemSchema),
  serviceCategories: z.array(PublicVendorListCategorySchema),
  portfolio: z.array(PublicVendorListPortfolioItemSchema),
  rateCard: z.array(PublicVendorListRateCardItemSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  equipmentItems: z.array(PublicVendorListCategorySchema),
});

/** PATCH body for workspace profile updates (partial). */
export const UpsertVendorWorkspaceProfileSchema = z
  .object({
    companyName: z.string().min(1).max(120).optional(),
    bio: z.string().min(1).max(2000).optional(),
    logoUrl: z.url().nullable().optional(),
    experienceLevel: z.enum(VendorExperienceLevel).optional(),
    yearsExperience: z.number().int().min(0).optional(),
    teamSize: z.enum(VendorTeamSize).nullable().optional(),
    serviceAreas: z.array(z.string()).optional(),
    maxDistanceKm: z.number().int().min(0).nullable().optional(),
    availabilityCalendar: z.record(z.string(), z.unknown()).nullable().optional(),
    isAvailable: z.boolean().optional(),
    visibleInMarketplace: z.boolean().optional(),
    equipmentManifest: z.array(WorkspaceEquipmentItemInputSchema).optional(),
    portfolio: z.array(WorkspacePortfolioItemInputSchema).optional(),
    rateCard: z.array(WorkspaceRateCardItemInputSchema).optional(),
    equipmentItemIds: z.array(z.uuid()).optional(),
    equipmentItemCodes: z.array(z.string().min(1).max(64)).optional(),
    categoryCodes: z.array(z.string()).optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field is required',
  });

/** POST body for submit-for-verification (explicit intent for signing). */
export const SubmitVendorForVerificationSchema = z.object({
  verificationStatus: z.literal(VendorVerificationStatus.SUBMITTED),
});

/** Query for `GET /users/me/vendor-profile` — `view=workspace` returns owner workspace shape. */
export const GetVendorProfileQuerySchema = z.object({
  view: z.literal('workspace').optional(),
});
